import React, { useState, useContext, useEffect, useMemo } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import useSections from "./useSections.js";
import NewSectionDraggable from "./NewSectionDraggable.js";
import TemplatesModal from "../templates/TemplatesModal";
import useTemplates from "../templates/useTemplates";
import CFPreviewCard from "../shared/CFPreviewCard";

const NewSection = (props) => {
  const [contextData] = useContext(VariantContextData);

  let templates = props.section_templates;
  const website = contextData.objects.website;

  const { Sections } = useSections();
  const { Templates } = useTemplates();

  const [searchText, setSearchText] = useState("");
  const [templateTags, setTemplateTags] = useState([]);
  const [selectedTagFilter, setSelectedTagFilter] = useState({});

  const [remixTemplatesModalOpen, setRemixTemplatesModalOpen] = useState(false);

  const searchedTemplates = templates.filter(function (template) {
    return template.name.toLowerCase().includes(searchText.toLowerCase());
  });

  const createSection = function (template_id = 0) {
    const stepToUse = contextData.new_object.step_id
      ? Object.values(contextData.objects.variant.steps).filter(
          (step) => step.id == contextData.new_object.step_id
        )[0]
      : Object.values(contextData.objects.variant.steps).filter(
          (step) => step.toBeDeleted !== true && step.position == 1
        )[0];

    if (stepToUse) {
      if (template_id > 0) {
        Sections.create(stepToUse, null, template_id);
      } else {
        Sections.create(stepToUse);
      }
    }
  };

  const templatesGroupedByTagName = useMemo(() => {
    const results = {};

    templateTags.forEach((tag) => {
      let filteredTemplates = templates.filter((template) => {
        return template.template_tags.some((temp_tag) => {
          return temp_tag.name === tag.name;
        });
      });

      results[humanizeStr(tag.name)] = filteredTemplates.sort(
        (a, b) => b.times_used - a.times_used
      );
    });

    return results;
  }, [templates, templateTags]);

  const myTemplates = useMemo(() => {
    return templates.filter((t) =>
      t.accounts?.find((acc) => acc.id === website?.account_id)
    );
  }, [templates, website]);

  const HeadlineWithButton = ({ title, onClick = () => {} }) => {
    return (
      <div className="w-full text-xl text-black dark:text-white flex justify-between">
        <div className="font-semibold">{title}</div>

        <div
          className="text-base text-gray-500 flex items-center gap-3 cursor-pointer"
          onClick={onClick}
        >
          See All
          <svg
            width="7"
            height="10"
            viewBox="0 0 7 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.72786 1.60419L5.26953 5.14585L1.72786 8.68752"
              stroke="black"
              strokeOpacity="0.5"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    );
  };

  useEffect(() => {
    Templates.getTemplateTags().then((response) => {
      setTemplateTags(response);

      NewSectionDraggable.start(contextData, Sections);
    });
  }, []);

  return (
    <React.Fragment>
      <div id="new-section" className="row hard-center my-4">
        <div className="w-full">
          <HeadlineWithButton
            title="Section Templates"
            onClick={() => {
              setSelectedTagFilter({});
              setRemixTemplatesModalOpen(true);
            }}
          />

          <div
            className="w-full mt-2 relative border-solid border-0 border-slate-200 mb-[2rem]"
            style={{ borderBottomWidth: "1px" }}
          >
            <span className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400">
              <i className="fas fa-search text-2xl"></i>
            </span>

            <input
              type="text"
              name="name"
              className="w-full px-2 py-4 pl-8 text-xl rounded-lg text-slate-500 dark:focus:color-white bg-white dark:bg-slate-950  border-none focus:border-none focus:outline-none"
              placeholder="Search..."
              defaultValue={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          {(!searchText || searchText.trim() === "") && (
            <div className="w-full p-2" id="sections-create-from-scratch">
              <div
                className="cursor-pointer w-full text-xl border-solid rounded-lg bg-slate-950 dark:bg-blue-600 border-slate-950 dark:border-blue600 text-white py-5 flex items-center justify-center gap-3"
                onClick={() => {
                  setSelectedTagFilter({});
                  setRemixTemplatesModalOpen(true);
                }}
              >
                <svg
                  width="21"
                  height="15"
                  viewBox="0 0 21 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.8358 1C16.7176 1 17.4325 1.63784 17.4325 2.42465L17.4325 4.85661C17.4325 5.64343 16.7177 6.28127 15.8358 6.28127H13.4408C12.559 6.28127 11.8442 5.64343 11.8442 4.85661L11.8442 2.42465C11.8442 1.63784 12.559 1 13.4408 1L15.8358 1Z"
                    stroke="white"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.65917 1C3.77735 1 3.0625 1.63784 3.0625 2.42465L3.06251 4.85661C3.06251 5.64343 3.77736 6.28127 4.65917 6.28127H7.05417C7.93599 6.28127 8.65084 5.64343 8.65084 4.85661L8.65083 2.42465C8.65083 1.63784 7.93598 1 7.05417 1L4.65917 1Z"
                    stroke="white"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.8358 8.71876C16.7177 8.71876 17.4325 9.3566 17.4325 10.1434V12.5753C17.4325 13.3622 16.7177 14 15.8358 14H13.4408C12.559 14 11.8442 13.3622 11.8442 12.5753L11.8442 10.1434C11.8442 9.3566 12.559 8.71876 13.4408 8.71876H15.8358Z"
                    stroke="white"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.65917 8.71876C3.77736 8.71876 3.06251 9.3566 3.06251 10.1434L3.06251 12.5753C3.06251 13.3622 3.77736 14 4.65917 14H7.05417C7.93599 14 8.65084 13.3622 8.65084 12.5753L8.65084 10.1434C8.65084 9.3566 7.93599 8.71876 7.05417 8.71876H4.65917Z"
                    stroke="white"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Section Templates
              </div>

              <div
                className="cursor-pointer w-full h-[50px] mt-4 mb-9 text-center leading-[50px] text-lg rounded-lg text-slate-500 hover:text-blue-600 dark:hover:text-white bg-white hover:bg-white dark:bg-slate-950 dark:hover:bg-slate-700 border-dashed border-[1px] border-slate-300 hover:border-blue-600 dark:border-slate-500 dark:hover:border-white"
                onClick={createSection}
              >
                + Create From Scratch
              </div>

              {myTemplates.length > 0 && (
                <React.Fragment>
                  <HeadlineWithButton
                    title="My Templates"
                    onClick={() => {
                      setSelectedTagFilter({ myTemplates: true });
                      setRemixTemplatesModalOpen(true);
                    }}
                  />

                  <div className="flex gap-3 mt-3 mb-[2.5rem] new-section-templates-sortable-container">
                    {myTemplates.slice(0, 2).map((template, index) => (
                      <CFPreviewCard
                        objectId={template.id}
                        key={`template-${index}-${template.id}`}
                        imageUrl={template.screenshot}
                        buttonLabel="Use Template"
                        title={template.name}
                        onClick={() => createSection(template.id)}
                        style="boxed"
                        className="cf-draggable-section-template basis-1/2 flex-shrink-0 flex-grow-0"
                      />
                    ))}
                    {myTemplates.length === 1 && (
                      <div className="basis-1/2"></div>
                    )}
                  </div>
                </React.Fragment>
              )}

              {templateTags
                .filter(
                  (tag) =>
                    templatesGroupedByTagName[humanizeStr(tag.name)].length >
                      0 &&
                    [
                      "Header",
                      "Product hero",
                      "Products feed",
                      "Headline",
                      "Background image",
                      "Benefit",
                      "Quiz",
                      "Bundle offer",
                      "Carousel",
                      "Collapse",
                      "Comparison table",
                      "Form",
                      "Listicle",
                      "Logo",
                      "Paragraph",
                      "Pricing plans",
                      "Progress bar",
                      "Testimonial",
                      "FAQ",
                      "Footer",
                    ].includes(tag.name)
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((tag) => (
                  <React.Fragment key={tag.id}>
                    <HeadlineWithButton
                      title={humanizeStr(tag.name)}
                      onClick={() => {
                        setSelectedTagFilter({ [tag.tag_type]: { [tag.name]: true } });
                        setRemixTemplatesModalOpen(true);
                      }}
                    />

                    <div className="flex gap-3 mt-3 mb-[2.5rem] new-section-templates-sortable-container">
                      {templatesGroupedByTagName[humanizeStr(tag.name)]
                        .slice(0, 2)
                        .map((template, index) => (
                          <CFPreviewCard
                            objectId={template.id}
                            key={`template-${index}-${template.id}`}
                            imageUrl={template.screenshot}
                            buttonLabel="Use Template"
                            title={template.name}
                            onClick={() => createSection(template.id)}
                            style="boxed"
                            className="cf-draggable-section-template basis-1/2 flex-shrink-0 flex-grow-0"
                          />
                        ))}
                      {templatesGroupedByTagName[humanizeStr(tag.name)].length === 1 && (
                        <div className="basis-1/2"></div>
                      )}
                    </div>
                  </React.Fragment>
                ))}
            </div>
          )}

          <div className="new-section-templates-sortable-container">
            {searchText &&
              searchedTemplates.map((item) => (
                <CFPreviewCard
                  objectId={item.id}
                  key={item.id}
                  imageUrl={item.screenshot}
                  buttonLabel="+ Create"
                  title={item.name}
                  style="boxed"
                  className="cf-draggable-section-template h-[150px] my-[20px]"
                  onClick={() => createSection(item.id)}
                />
              ))}
          </div>
        </div>

        <TemplatesModal
          website={website}
          isOpen={remixTemplatesModalOpen}
          onClose={() => {
            setSelectedTagFilter({});
            setRemixTemplatesModalOpen(false);
          }}
          templates={templates}
          onSelectTemplate={(sectionTemplate) => {
            createSection(sectionTemplate.id);
          }}
          initialFilters={selectedTagFilter}
        >
          <div className="flex justify-between items-center mt-6 mb-6">
            <h4 className="m-0">Choose a section template</h4>
          </div>
        </TemplatesModal>
      </div>
    </React.Fragment>
  );
};

export default NewSection;
